<template>
  <div
    class="viewer-content viewer"
    :class="optionEnabled === true && getGrid() ? 'height-grid' : 'height-layout'"
    :style="{ 'margin-bottom': spinSelectedPosition === 'underspin' ? '80px' : '0' }"
  >
    <div id="smv" ref="smv" class="Sirv">
      <template>
        <div
          v-for="item in defaultData"
          :key="item.url"
          :data-src="item.url"
          :data-alt="customImagesAlts"
          :data-type="item.type != null ? 'zoom' : ''"
        />
      </template>
    </div>
    <SpinButtons v-if="spinControlPanelStatus" />
  </div>
</template>
<script>
import SpinButtons from "@/widgets/common/SpinButtons";
import { mapGetters, mapState } from "vuex";
import { debounce } from "lodash";
import {
  clearFromVideoThumbnail,
  getVideoThumbnailOption,
  clearFromModelThumbnail,
  getModelCertainOptions,
  clearFromModelSkybox,
  clearFromModelEnvironment,
  clearFromModelSkyboxHeight,
  clearFromModelCameraOrbit,
  clearFromModelCameraTarget,
} from "@/utils/utils";
import { filterByVideoOptions } from "@/utils/filter-options";
import itemErrors from "@/store/observable/item-errors";
import optionEnabled from "@/store/observable/option-enabled";

import { modelOptions as options3DParameters } from "@/constants";

export default {
  name: "SMV",
  components: {
    SpinButtons,
  },

  data() {
    return {
      options: {
        viewer: { autostart: "off" },
      },
      imagesAltsOption: null,
      defaultData: [
        { url: "https://demo.sirv.com/demo/snug/pink-b-living.jpg", type: "zoom" },
        { url: "https://demo.sirv.com/demo/snug/fabric.jpg", type: "zoom" },
        { url: "https://demo.sirv.com/demo/snug/grey-shadow.jpg", type: "zoom" },
        { url: "https://demo.sirv.com/demo/snug/grey.spin", type: null },
      ],
      instanceDebounce: debounce(this.setOptions, 700),
    };
  },
  computed: {
    optionEnabled,
    ...mapState([
      "settings",
      "zoomEnable",
      "enableMobileOptions",
      "viewSMVMode",
      "inlineModeSMV/inlineModeSMV",
    ]),
    itemErrors,
    ...mapGetters({
      inlineModeSMV: "inlineModeSMV/inlineModeSMV",
      videoThumbnailStatus: "video/videoThumbnailStatus",
      modelThumbnailStatus: "model/modelThumbnailStatus",
      modelSkyboxStatus: "model/modelSkyboxStatus",
      modelEnvironmentStatus: "model/modelEnvironmentStatus",
      modelSkyboxHeightStatus: "model/modelSkyboxHeightStatus",
      modelCameraOrbitStatus: "model/modelCameraOrbitStatus",
      modelCameraTargetStatus: "model/modelCameraTargetStatus",
      spinSelectedPosition: "spinControl/spinControlSelectedPosition",
      spinControlPanelStatus: "spinControl/spinControlPanelStatus",
    }),

    customImagesAlts() {
      return (
        this.imagesAltsOption &&
        this.imagesAltsOption.includes(":Manual|") &&
        this.imagesAltsOption.split("|")[1].replace(";", "")
      );
    },

    customImagesAltsHtml() {
      return this.customImagesAlts && `data-alt="${this.customImagesAlts}"`;
    },
    findStatusError() {
      return itemErrors.get().filter((item) => item.added === true);
    },
  },
  created() {
    this.unwatch = this.$store.watch(
      (state) => {
        return {
          optionsCSS: state.settings.optionsCSS,
          options: state.settings.options,
          optionsMobile: state.settings.optionsMobile,
          smv: state.settings.smv,
          data: state.settings.data,
          viewSMVMode: state.viewSMVMode,
          videoThumbnailStatus: state.video.videoThumbnailStatus,
          modelThumbnailStatus: state.model.modelThumbnailStatus,
          modelSkyboxStatus: state.model.modelSkyboxStatus,
          modelEnvironmentStatus: state.model.modelEnvironmentStatus,
          modelSkyboxHeightStatus: state.model.modelSkyboxHeightStatus,
          modelCameraOrbitStatus: state.model.modelCameraOrbitStatus,
          modelCameraTargetStatus: state.model.modelCameraTargetStatus,
        };
      },
      (value) => {
        this.instanceDebounce(
          value.options,
          value.optionsMobile,
          value.smv,
          value.viewSMVMode,
          value.videoThumbnailStatus,
          value.modelThumbnailStatus,
          value.modelSkyboxStatus,
          value.modelEnvironmentStatus,
          value.modelSkyboxHeightStatus,
          value.modelCameraOrbitStatus,
          value.modelCameraTargetStatus,
          value.optionsCSS
        );
      }
    );
  },
  methods: {
    getGrid() {
      return this.settings.options.split(";").filter((it) => it === "layout.type:grid");
    },
    getHtmlZoom(type) {
      return (type === "spins" && this.zoomEnable) ||
        (type === "video" && this.zoomEnable) ||
        (type === "model" && this.zoomEnable)
        ? ""
        : this.zoomEnable
        ? ' data-type="zoom"'
        : "";
    },
    getPined(pin) {
      return pin ? ` data-pinned="${pin}"` : "";
    },
    setOptions(
      opts,
      mobileOptions,
      items,
      viewSMVMode,
      videoThumbnailStatus,
      modelThumbnailStatus,
      modelSkyboxStatus,
      modelEnvironmentStatus,
      modelSkyboxHeightStatus,
      modelCameraOrbitStatus,
      modelCameraTargetStatus,
      optsCSS
    ) {
      if (window.Sirv !== undefined) {
        if (items[0].type === "view") {
          this.$store.dispatch("changeViewSMVMode", true);
        }

        let itemsHTML = "";
        this.imagesAltsOption = opts.split("; ").find((it) => it.includes("image.alt:"));
        let options = opts
          .split("; ")
          .filter((it) => !it.includes("image.alt:"))
          .join("; ");
        const containerStyle = optsCSS.filter((it) => it.type === "container-styles");

        const modelOptions = [];

        options = options
          .split("; ")
          .filter((it) => {
            let [parameter, value] = it.split(":");
            let isOptionOf3DModel = options3DParameters.includes(parameter);
            isOptionOf3DModel && modelOptions.push(it);
            return !isOptionOf3DModel && it;
          })
          .join("; ");

        let title, description;
        [title, description, options] = filterByVideoOptions(options);

        // Process slides with error
        let results = [];

        if (this.findStatusError.length) {
          if (this.sameMembers(this.findStatusError, items) === true) return this.loadError(items);

          Sirv.on("viewer:ready", (sirv) => {
            results = this.findStatusError.filter(({ src: src1 }) =>
              items.some(({ src: src2 }) => src1 === src2)
            );
            results.forEach((elem, i) => {
              if (document.querySelector(`div[data-src-type="${elem.src}"]`) === null) {
                sirv.insertItem(
                  `<div data-src="" data-src-type="${elem.src}" data-type="error" data-image-orientation="horizontal">${elem.statusCode}</div>`,
                  i + 1
                );
              }
            });

            if (!!this.$refs.smv) {
              let sliderElements = this.$refs.smv.querySelectorAll(
                '.smv-slide > .smv-content > div[data-type="error"]'
              );
              results.forEach((elem, i) => {
                sliderElements[i].innerHTML =
                  `<div class="load-error">
                  <span>File cannot be loaded ${elem.statusCode}</span>` +
                  `<div><a href="${elem.src}" target="_blank" class="external">${elem.src}</a><a href="${elem.src}" target="_blank" class="external fas fa-external-link-alt"></a></div>` +
                  `</div>`;
              });

              if (sirv.itemsCount() > 1) {
                this.$refs.smv.querySelector(".smv").classList.add("smv-selectors-bottom");
              }
            }
          });
        } else {
          results = [];
          Sirv.off("viewer:ready");

          Sirv.on("viewer:ready", (sirv) => {
            let sliderElements = this.$refs.smv.querySelectorAll(
              '.smv-slide > .smv-content > div[data-type="error"]'
            );
            sliderElements.forEach((elem, i) => {
              sirv.removeItem(1);
              sliderElements[i].remove();
            });
          });
        }
        // End process slides with error

        window.Sirv.stop();

        const dataOptions = "data-options";
        const dataMobileOptions = "data-mobile-options";
        const dataSrc = "data-src";
        const videoThumbnailOption = this.videoThumbnailStatus
          ? `data-options="thumbnail: ${getVideoThumbnailOption(options)}"`
          : "";

        const modelCertainOptions =
          this.modelThumbnailStatus || this.modelSkyboxStatus || this.modelEnvironmentStatus
            ? `data-options="${getModelCertainOptions(options)}"`
            : "";

        if (!!this.$refs.smv) {
          this.$refs.smv.setAttribute(
            dataOptions,
            clearFromVideoThumbnail(options, this.videoThumbnailStatus)
          );
          this.$refs.smv.setAttribute(
            dataOptions,
            clearFromModelThumbnail(options, this.modelThumbnailStatus)
          );

          // this.$refs.smv.setAttribute(
          //   dataOptions,
          //   clearFromModelSkybox(options, this.modelSkyboxStatus)
          // );

          // this.$refs.smv.setAttribute(
          //   dataOptions,
          //   clearFromModelEnvironment(options, this.modelEnvironmentStatus)
          // );

          // this.$refs.smv.setAttribute(
          //   dataOptions,
          //   clearFromModelSkyboxHeight(options, this.modelSkyboxHeightStatus)
          // );

          // this.$refs.smv.setAttribute(
          //   dataOptions,
          //   clearFromModelCameraOrbit(options, this.modelCameraOrbitStatus)
          // );

          // this.$refs.smv.setAttribute(
          //   dataOptions,
          //   clearFromModelCameraTarget(options, this.modelCameraTargetStatus)
          // );

          this.$refs.smv.setAttribute(dataMobileOptions, mobileOptions);

          if (containerStyle.length)
            this.$refs.smv.setAttribute(
              "style",
              containerStyle.map((it) => `${it.parameter}${it.separator} ${it.value}`).join("; ")
            );
          else this.$refs.smv.removeAttribute("style");
        }

        if (
          viewSMVMode === false &&
          videoThumbnailStatus === false &&
          modelThumbnailStatus === false &&
          modelSkyboxStatus === false &&
          modelEnvironmentStatus === false &&
          modelSkyboxHeightStatus === false &&
          modelCameraOrbitStatus === false &&
          modelCameraTargetStatus === false
        ) {
          try {
            this.$refs.smv.removeAttribute(dataSrc);
          } catch (e) {
            console.log(e);
          }

          if (this.$refs.smv) {
            itemsHTML = items
              .map((smvItem) => {
                return smvItem.type !== "spins"
                  ? smvItem.type !== "video" && smvItem.type !== "model"
                    ? `<div ${this.getPined(smvItem.pined)} ${
                        this.customImagesAltsHtml
                      } data-src="${smvItem.src}" ${this.getHtmlZoom(smvItem.type)}></div>`
                    : smvItem.type === "video"
                    ? `<div ${this.getPined(smvItem.pined)} ${title} ${description} data-src="${
                        smvItem.src
                      }"></div>`
                    : smvItem.type === "model"
                    ? `<div ${this.getPined(smvItem.pined)} ${
                        this.customImagesAltsHtml
                      } ${this.getHtmlZoom(smvItem.type)} ${
                        !modelOptions.length
                          ? ""
                          : 'data-options="' +
                            modelOptions.join("; ").replaceAll("thumbnail", "model.thumbnail") +
                            '"'
                      } data-src="${smvItem.src}"></div>`
                    : `<div ${this.getPined(smvItem.pined)} ${
                        this.customImagesAltsHtml
                      } data-src="${smvItem.spinsrc}"></div>`
                  : `<div ${this.getPined(smvItem.pined)} ${this.customImagesAltsHtml} data-src="${
                      smvItem.spinsrc
                    }"></div>`;
              })
              .join("");

            this.$refs.smv.innerHTML = itemsHTML;
          }
        } else if (videoThumbnailStatus) {
          if (this.$refs.smv) {
            this.$refs.smv.innerHTML = items
              .map((smvItem) =>
                smvItem.type !== "spins"
                  ? smvItem.type !== "video"
                    ? `<div ${this.getPined(smvItem.pined)} ${
                        this.customImagesAltsHtml
                      } data-src="${smvItem.src}" ${this.getHtmlZoom(smvItem.type)}></div>`
                    : `<div ${this.getPined(smvItem.pined)} ${title} ${description} data-src="${
                        smvItem.src
                      }" ${videoThumbnailOption}></div>`
                  : `<div ${this.getPined(smvItem.pined)} data-src="${smvItem.spinsrc}"></div>`
              )
              .join("");
          }
        } else if (
          modelThumbnailStatus ||
          modelSkyboxStatus ||
          modelEnvironmentStatus ||
          modelSkyboxHeightStatus ||
          modelCameraOrbitStatus ||
          modelCameraTargetStatus
        ) {
          if (this.$refs.smv) {
            this.$refs.smv.innerHTML = items
              .map((smvItem) =>
                smvItem.type !== "spins"
                  ? smvItem.type !== "model"
                    ? `<div ${this.getPined(smvItem.pined)} ${
                        this.customImagesAltsHtml
                      } data-src="${smvItem.src}" ${this.getHtmlZoom(smvItem.type)}></div>`
                    : `<div ${this.getPined(smvItem.pined)} ${title} ${description} data-src="${
                        smvItem.src
                      }" ${modelCertainOptions}></div>`
                  : `<div ${this.getPined(smvItem.pined)} data-src="${smvItem.spinsrc}"></div>`
              )
              .join("");
          }
        } else {
          this.$refs.smv.setAttribute(dataSrc, items.map((item) => item.src).join(", "));
          if (viewSMVMode === true) {
            let opts = options
              .split(";")
              .filter((it) => Boolean(it))
              .map((it) => it.trim());
            let disableZoom = opts.findIndex((it) => it === "zoom.enable:false");
            if (disableZoom > -1) {
              opts[disableZoom] = "zoom.ratio:0";
            }
            if (opts.length > 0) {
              this.$refs.smv.setAttribute(dataOptions, opts.join("; "));
            }
          }
        }

        window.Sirv.start();
      }
    },
    loadError(items) {
      // Template error
      Sirv.on("viewer:ready", (sirv) => {
        let results = this.findStatusError.filter(({ src: id1 }) =>
          items.some(({ src: id2 }) => id2 === id1)
        );

        if (this.sameMembers(this.findStatusError, items) === false)
          return debounce(this.setOptions, 700);

        let errorLinks = "";

        this.$refs.smv.querySelector("div").style.top = "40%";
        this.$refs.smv.querySelector("div").style.height = "auto !important";
        this.$refs.smv.querySelector("div").style.position = "relative";
        results.map((smvItem) => {
          errorLinks += `<div><a href="${smvItem.src}" target="_blank" class="external">${smvItem.src}</a> ${smvItem.statusCode} <a href="${smvItem.src}" target="_blank" class="external fas fa-external-link-alt"></a></div>`;
        });

        this.$refs.smv.querySelector(
          "div"
        ).innerHTML = `<div class="load-error" style="color: #535962;">
          <span>File cannot be loaded</span>
            ${errorLinks}
          </div>`;
      });
    },
    sameMembers(arr1, arr2) {
      return arr2.every(({ src: src1 }) => arr1.some(({ src: src2 }) => src1 === src2));
    },
    // getUniqueListBy(arr, key) {
    //   return [...new Map(arr.map(item => [item[key], item])).values()]
    // }
  },
};
</script>

<style lang="scss">
.smv.smv-selectors-bottom:has(> div.load-error:first-child) {
  height: auto !important;
}
.load-error {
  word-wrap: break-word;

  span {
    margin-bottom: 16px;
    line-height: 1;
    font-size: 20px;
    bottom: 10px;
    position: relative;
  }

  a {
    color: inherit;
  }

  div {
    line-height: 1.24;
    margin-top: 10px;
  }

  .fas {
    margin-left: 5px;
  }
}

.external[target="_blank"] {
  margin-right: 10px;
}

.external[target="_blank"]::after {
  display: none !important;
}

.smv .smv-item div[data-type="error"] {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: #f56c6c;
}
</style>

<style lang="scss" scoped>
// #smv[data-options*="layout.type:grid"] .smv-grid-gallery {
//   height: 100%;
// }

// #smv[data-options*="layout.type:grid"] {
//   overflow-y: auto;
// }
#smv[data-options*="layout.type:grid"] {
  transform: none;
}
.viewer[data-options*="grid"] {
  height: 100%;
}

.viewer {
  position: relative;
  @media only screen and (min-width: 1200px) {
    width: 400px;
    height: 500px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 1300px) {
    width: 400px;
    height: 500px;
  }
  @media only screen and (min-width: 1430px) {
    width: 630px;
    height: 500px;
  }
}

.height-layout {
  height: 500px;
}
.height-grid {
  // min-height: 500px;
  height: inherit;
  // height: 100%;
}
</style>
